/**
 * 1.3.1 Administration → Media portal → Branding → Logo and accent colour (Q1): upload a PNG or SVG
 * (PORTAL_LOGO_TYPES, at most PORTAL_LOGO_MAX_BYTES; PUT /api/v1/admin/portal/logo), remove it, and the accent colour
 * with its contrast against white (accentContrastOk; refused below PORTAL_ACCENT_MIN_CONTRAST). Rendered by
 * PortalBranding (Settings.tsx). Q1's file.
 */
import { useEffect, useRef, useState } from 'react';
import { ImageUp, Save, Trash2 } from 'lucide-react';
import { PORTAL_ACCENT_MIN_CONTRAST, PORTAL_LOGO_MAX_BYTES, PORTAL_LOGO_TYPES, accentContrastOk, type PortalLogoDTO, type PortalSettingsDTO } from '@prism/contracts';
import { ApiError, api, errorDetail } from '../../../api/client';
import { usePortalAdminRefresh, usePortalSettings } from '../../../api/hooks/portalAdmin';
import { toast } from '../../../components/Toasts';

const HEX = /^#[0-9a-fA-F]{6}$/;

/** The logo as PRISM's own host serves it to staff (the portal host's URL is refused here), with the same ?v=. */
const previewUrl = (logoUrl: string) => logoUrl.replace('/api/v1/portal/logo', '/api/v1/admin/portal/logo');

export function PortalLogo() {
  const s = usePortalSettings().data;
  const refresh = usePortalAdminRefresh();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [accent, setAccent] = useState('');
  useEffect(() => { if (s) setAccent(s.branding.accentColor ?? ''); }, [s]);
  if (!s) return null;
  const logoUrl = s.branding.logoUrl;

  async function upload(file: File) {
    setProblem(null);
    const type = file.type || (file.name.toLowerCase().endsWith('.svg') ? 'image/svg+xml' : '');
    if (!(PORTAL_LOGO_TYPES as readonly string[]).includes(type)) { setProblem('Choose a PNG or an SVG file.'); return; }
    if (file.size > PORTAL_LOGO_MAX_BYTES) { setProblem(`The logo can be at most ${PORTAL_LOGO_MAX_BYTES / 1024} KiB; this one is ${Math.ceil(file.size / 1024)} KiB.`); return; }
    setBusy(true);
    try {
      await api<PortalLogoDTO>('/api/v1/admin/portal/logo', { method: 'PUT', headers: { 'content-type': type }, body: file });
      await refresh();
      toast('Logo saved');
    } catch (err) {
      setProblem(err instanceof ApiError && err.code === 'LOGO_INVALID' ? err.message : errorDetail(err));
    } finally {
      setBusy(false);
      if (input.current) input.current.value = '';
    }
  }

  async function remove() {
    if (!window.confirm('Remove the logo? The portal and its emails show the portal’s name instead.')) return;
    setBusy(true);
    try {
      await api('/api/v1/admin/portal/logo', { method: 'DELETE' });
      await refresh();
      toast('Logo removed');
    } catch (err) {
      toast(errorDetail(err), 'error');
    } finally {
      setBusy(false);
    }
  }

  const accentValid = accent === '' || HEX.test(accent);
  const contrast = accent && HEX.test(accent) ? accentContrastOk(accent) : null;
  const accentDirty = accent !== (s.branding.accentColor ?? '');
  async function saveAccent() {
    setBusy(true);
    try {
      await api<PortalSettingsDTO>('/api/v1/admin/portal/settings', { method: 'PATCH', json: { accentColor: accent || null } });
      await refresh();
      toast(accent ? 'Accent colour saved' : 'Accent colour reset to the default');
    } catch (err) {
      toast(errorDetail(err), 'error');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="pb-extra" data-testid="portal-logo">
      <h4>Logo and accent colour</h4>
      <div className="pb-logo-row">
        <div className="pb-logo-box" aria-label="The logo now">
          {logoUrl ? <img src={previewUrl(logoUrl)} alt="The portal’s logo" data-testid="portal-logo-preview" /> : <span className="faint">No logo</span>}
        </div>
        <div className="pb-logo-actions">
          <input ref={input} type="file" accept=".png,.svg,image/png,image/svg+xml" className="sr-only" id="pb-logo-file"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); }} data-testid="portal-logo-file" />
          <button className="btn sm" disabled={busy} onClick={() => input.current?.click()} data-testid="portal-logo-upload"><ImageUp size={14} /> {logoUrl ? 'Replace logo…' : 'Upload logo…'}</button>
          {logoUrl && <button className="btn ghost sm" disabled={busy} onClick={() => void remove()} data-testid="portal-logo-remove"><Trash2 size={14} /> Remove</button>}
          <span className="help">PNG or SVG, at most {PORTAL_LOGO_MAX_BYTES / 1024} KiB; shown on the sign-in page, at the top of the portal and in its emails. Scripts, links and anything an SVG loads from elsewhere are removed. Some email programs do not show SVG images; a PNG shows everywhere.</span>
          {problem && <p className="portal-bad pb-problem" role="alert" data-testid="portal-logo-problem">{problem}</p>}
        </div>
      </div>
      <div className="field">
        <label htmlFor="pb-accent">Accent colour</label>
        <div className="pb-accent-row">
          <input type="color" aria-label="Pick the accent colour" value={HEX.test(accent) ? accent : '#5b47e0'} onChange={(e) => setAccent(e.target.value)} />
          <input id="pb-accent" className={`input mono pb-accent-hex ${accentValid ? '' : 'invalid'}`} value={accent} placeholder="Default" maxLength={7}
            onChange={(e) => setAccent(e.target.value.trim())} data-testid="portal-accent" />
          {contrast && (
            <span className={`pb-swatch ${contrast.ok ? 'ok' : 'bad'}`} data-testid="portal-accent-contrast" data-ok={contrast.ok}>
              <span className="pb-swatch-chip" style={{ background: accent }}>Aa</span>
              {contrast.ratio}:1 {contrast.ok ? 'readable' : `too light, at least ${PORTAL_ACCENT_MIN_CONTRAST}:1 needed`}
            </span>
          )}
        </div>
        <span className="help">The portal’s buttons and links. White text sits on it, so it must reach a contrast of {PORTAL_ACCENT_MIN_CONTRAST}:1 against white (WCAG AA); a lighter colour is refused. Empty: the portal’s own colour.</span>
      </div>
      <div className="portal-save-row">
        <button className="btn" disabled={!accentDirty || !accentValid || (contrast !== null && !contrast.ok) || busy} onClick={() => void saveAccent()} data-testid="portal-accent-save"><Save size={14} /> Save colour</button>
      </div>
    </div>
  );
}
